import { mixins } from 'vue-class-component';
import { Component, Emit, Inject, Vue } from 'vue-property-decorator';
import Vue2Filters from 'vue2-filters';
import AlertMixin from '@/shared/alert/alert.mixin';
import { IExamSession } from '@/shared/model/exam-session.model';
import { ExamType } from '@/shared/model/enumeration/examType.model';
import { required } from 'vuelidate/lib/validators';
import ExamSessionService from '@/bo/pages/entities/exam-session/exam-session.service';
import AutocompletionLocationService from '@/fo/components/autocompletion-location/autocompletion-location.service';

//Input examDate value must be at least today's date. setHours(0, 0, 0, 0) -> compare only on date and not on time
const mustBeAtLeastToday = value => new Date(value).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);

@Component({
  mixins: [Vue2Filters.mixin],
  validations: {
    inputLocation: {
      required,
    },
    inputExamType: {},
    inputExamDate: {
      mustBeAtLeastToday,
    },
  },
})
export default class SearchSessionsForm extends mixins(AlertMixin) {
  @Inject('examSessionService')
  private examSessionService: () => ExamSessionService;
  @Inject('autocompletionLocationService')
  private autocompletionLocationService: () => AutocompletionLocationService;

  public examSessions: IExamSession[] = [];

  public examTypes = Object.keys(ExamType);

  public inputLocation: string = '';
  public inputLocationValue: string = '';
  public inputExamType: string = 'MARITIME';
  public inputExamDate: string = new Date().toISOString().slice(0, 10);

  //values emitted by child component autocompletionLocation
  public positionName: string = '';
  public latitude: number;
  public longitude: number;

  public error: boolean = false;
  public showError: boolean = false;

  /**
   * When text changes in input Location, inputLocationValue changes.
   * InputLocationValue is used for autocompletion purpose (-> prop of child component autocompletionLocation)
   */
  public onChangeInputLocation() {
    this.inputLocationValue = this.inputLocation;
  }

  /**
   * Retrieve location selected by user emitted by child component autocompletionLocation
   * @param locationSelected result of autocompletion selected by user
   */
  public setSelectedLocation(locationSelected) {
    this.inputLocation = locationSelected.inputLocation;
    this.positionName = locationSelected.city;
    this.latitude = locationSelected.latitude;
    this.longitude = locationSelected.longitude;
  }

  /**
   * On form submit, validate inputs and emit search criteria to parent component
   */
  public submit() {
    if (this.validateForm()) {
      this.retrieveAllExamSessionsByCriteria(this.latitude, this.longitude, this.inputExamType, this.inputExamDate);
    } else {
      this.showError = true;
    }
  }

  /**
   * Retrieve all examSessions by the location, exam type and exam date selected by user.
   * @param latitude
   * @param longitude
   * @param examType
   * @param examDate
   */
  public retrieveAllExamSessionsByCriteria(latitude: number, longitude: number, examType: string, examDate: string): void {
    this.examSessionService()
      .retrieveByCriteria(latitude, longitude, examType, examDate)
      .then(
        res => {
          this.error = false;
          this.examSessions = res.data;
          this.emitExamSessions(this.examSessions);
        },
        err => {
          this.error = true;
        }
      );
  }

  /**
   * Emit examSessions to parent component
   * @param examSessions
   */
  public emitExamSessions(examSessions: IExamSession[]) {
    // use of eventHub declared globally in main.ts
    // @ts-ignore
    this.$eventHub.$emit('submitSearchSessionsForm', {
      examSessions: this.examSessions,
      selectedLatitude: this.latitude,
      selectedLongitude: this.longitude,
      selectedPositionName: this.positionName,
      selectedExamType: this.inputExamType,
      selectedExamDate: this.inputExamDate,
    });
  }

  /**
   * Validate form, return true if form is valid
   */
  public validateForm(): boolean {
    let isValid: boolean = true;
    this.$v.$touch();
    if (this.$v.$invalid) {
      isValid = false;
    }
    return isValid;
  }

  /**
   * Get user location on click on "Me localiser"
   */
  async getUserLocation() {
    let coords = await this.getPosition();
    this.latitude = coords['latitude'];
    this.longitude = coords['longitude'];
  }

  /**
   * Call to Geolocation API
   */
  getPosition = () => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position => {
          resolve(position.coords);
          this.retrieveCityByLatAndLong(String(position.coords.latitude), String(position.coords.longitude));
        },
        err => {
          alert('Votre navigateur ne permet pas la géolocalisation');
        }
      );
    });
  };

  /**
   * Call to autocompletionService to retrieve position name by latitude and longitude
   */
  public retrieveCityByLatAndLong(lat: string, long: string): void {
    this.autocompletionLocationService()
      .retrieveLocationsByLatLong(lat, long)
      .then(
        res => {
          let city = res;
          this.inputLocation = 'Ma position (' + city + ')';
          this.positionName = 'Ma position (' + city + ')';
        },
        err => {
          this.inputLocation = 'Ma position';
          this.positionName = 'Ma position';
        }
      );
  }
}
